import { URLs } from "services/settings";

/**
 * Reports our content height to the ConnectAmericas page that embeds us, so it can size its
 * iframe to us and drop the inner scrollbar.
 *
 * We are served from a subdomain (incoterms.connectamericas.com) while the parent lives on
 * connectamericas.com, so the parent cannot read our document — `contentDocument` throws a
 * SecurityError across origins. postMessage is the only channel. The protocol, whose parent
 * half ships in whitelabel-builder's Iframe block:
 *
 *   parent -> us      { type: "requestResize" }             asks us for our height
 *   us     -> parent  { type: "resize", height: <number> }  content height in CSS pixels
 *
 * The parent ignores height deltas of 2px or less and bails out of its re-render, so posting
 * on every observed change cannot drive a resize loop.
 */

const RESIZE = "resize";
const REQUEST_RESIZE = "requestResize";

/** Matches the parent's own epsilon: below this a report is noise, not a layout change. */
const HEIGHT_EPSILON = 2;

let bridgeStarted = false;

/**
 * Measures with `getBoundingClientRect().height` and deliberately not `scrollHeight` or
 * `clientHeight`. On the root element those two are floored at the viewport height — and our
 * viewport, inside the iframe, IS the height the parent set from our last report. Reporting
 * them would make the height monotonic: it grows to fit content and can then never shrink
 * back, so a collapsed accordion or a cleared result would leave a permanent gap.
 */
const measureContentHeight = () => {
  const { documentElement: html, body } = document;

  if (!html || !body) return 0;

  return Math.ceil(Math.max(html.getBoundingClientRect().height, body.getBoundingClientRect().height));
};

export function initIframeResizeBridge() {
  // Standalone (opened directly, not embedded): nothing to report to, stay silent.
  if (typeof window === "undefined" || window.parent === window) return;
  if (bridgeStarted) return;

  const allowedParentOrigins = URLs.parentOrigins;
  if (!allowedParentOrigins.length) return;

  bridgeStarted = true;

  // Lets our CSS drop the `height: 100%` chain while framed. With the root element pinned to
  // the viewport height, "content height" and "iframe height" are the same number by
  // definition and no measurement — ours or the parent's — can ever grow the frame.
  document.documentElement.setAttribute("data-framed", "true");

  let lastReportedHeight = 0;
  let postQueued = false;

  const postHeight = (force) => {
    const height = measureContentHeight();
    if (!height) return;
    if (!force && Math.abs(height - lastReportedHeight) < HEIGHT_EPSILON) return;

    lastReportedHeight = height;

    // Explicit origins, never "*": "*" would hand our layout dimensions to any site that
    // frames us. Only the origin we are actually inside receives the message; postMessage
    // silently drops the rest.
    allowedParentOrigins.forEach((origin) => {
      window.parent.postMessage({ type: RESIZE, height }, origin);
    });
  };

  // Bursts (a MutationObserver batch, a resize drag, observers firing together) collapse into
  // a single post per frame, measured after the browser has laid the changes out.
  const schedulePostHeight = () => {
    if (postQueued) return;
    postQueued = true;

    window.requestAnimationFrame(() => {
      postQueued = false;
      postHeight(false);
    });
  };

  window.addEventListener("message", (event) => {
    if (!allowedParentOrigins.includes(event.origin)) {
      // An embedding host missing from the allowlist is otherwise invisible: we simply never
      // answer, and the parent silently keeps its fallback height. Say so instead.
      if (event.data && event.data.type === REQUEST_RESIZE) {
        console.warn(
          `[iframeResizeBridge] ignoring ${REQUEST_RESIZE} from "${event.origin}": not in REACT_APP_PARENT_ORIGINS (${allowedParentOrigins.join(", ")})`,
        );
      }
      return;
    }

    if (event.data && event.data.type === REQUEST_RESIZE) postHeight(true);
  });

  // Fonts, images and the estimator template all land after DOMContentLoaded.
  window.addEventListener("load", () => postHeight(true));
  window.addEventListener("resize", schedulePostHeight);

  if (typeof window.ResizeObserver !== "undefined") {
    const resizeObserver = new ResizeObserver(schedulePostHeight);
    resizeObserver.observe(document.documentElement);
    resizeObserver.observe(document.body);
  }

  // Catches what resizes no observed box directly: section accordions and group tabs opening,
  // async estimator/history data arriving, MUI dialogs mounting, wouter route changes.
  const mutationObserver = new MutationObserver(schedulePostHeight);
  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "style", "hidden"],
  });

  // Answer the parent's first handshake even if it arrived before this ran.
  postHeight(true);
}

// Started on import rather than from a call in index.jsx, so that the bridge is running before
// the app's own modules evaluate: ES imports are evaluated before the importing module's body,
// so anything that throws at module scope in the App tree (a missing GA id, for one) would
// otherwise leave the parent stuck at its fallback height with no way to find out why.
initIframeResizeBridge();
